/** @jsxImportSource @emotion/react */
import { COLORS } from '@btime/constants'
import { css } from '@emotion/react'
import { isBoolean } from '@btime/helpers'
import { useOpenCloseActions } from '@btime/react-contexts'
import { useTranslation } from 'react-i18next'
import React from 'react'

import { SmallText } from '../presentation/Text'
import { WrapLoading } from '../helpers/Loading'
import DropdownLink from '../navigation/DropdownLink'
import FlexGrid from '../structure/FlexGrid'
import Icon from '../../packages/kiti/components/shapes/Icon'
import Link from '../actions/Link'
import fontSizes from '../../utils/constants/fontSizes'

export const TableColumn = props => <SmallText color={COLORS.dark3} breakWord {...props} />

const RowsActionsColumn = ({ useActions, record }) => {
  const { t } = useTranslation()
  const actions = useActions(record)
  const [options, loading] = actions || []
  const isUsingLoading = isBoolean(loading)

  return (
    <WrapLoading loading={isUsingLoading && loading}>
      <DropdownLink
        fromRight
        t={t}
        actionContent={<Icon name="BsThreeDots" />}
        options={isUsingLoading ? options : actions}
      ></DropdownLink>
    </WrapLoading>
  )
}

export const useRowActionsColumn = useActions => (value, record, index) =>
  <RowsActionsColumn record={record} useActions={useActions} />

export const useClickableColumn = onClick => (value, record, index) =>
  (
    <Link onClick={() => onClick(value, record, index)}>
      <SmallText strong color={COLORS.dark2} breakWord>
        {value}
      </SmallText>
    </Link>
  )

export const IconCountColumn = ({ value, tooltip, icon, onClick, color }) => {
  const hasValue = value > 0
  const finalColor = color || (hasValue ? COLORS.purple : COLORS.light3)

  return (
    <Link onClick={() => hasValue && onClick()}>
      <FlexGrid gutter={0.15} justify="flex-start">
        <Icon name={icon} size="1rem" color={finalColor} />
        <SmallText bold color={finalColor}>
          {value}
        </SmallText>
      </FlexGrid>
    </Link>
  )
}
export const useIconCountColumn =
  (onClick, tooltip, icon = 'BsListTask') =>
  (value, record, index) =>
    <IconCountColumn icon={icon} value={value} tooltip={tooltip} onClick={() => onClick(value, record)} />

const ExpandableColumn = props => {
  const { toggle } = useOpenCloseActions()
  // if (!value) return false
  return <IconCountColumn {...props} onClick={toggle} />
}
export const useExpandableColumn =
  (tooltip, icon = 'BsListTask') =>
  (value, record, index) =>
    <ExpandableColumn icon={icon} value={value} tooltip={tooltip} />

export const genColoredColumn = color => value =>
  (
    <TableColumn color={color} strong>
      {value}
    </TableColumn>
  )
export const genFormattedColumn = formatter => (value, record) => <TableColumn>{formatter(value, record)}</TableColumn>

export const Head = ({ children }) => <thead>{children}</thead>
export const Body = ({ children }) => <tbody>{children}</tbody>
export const Row = ({ children }) => <tr>{children}</tr>

export const Col = ({ head, width, ...props }) => {
  const style = { width }
  return head ? <th style={style} {...props} /> : <td style={style} {...props} />
}

const Table = props => {
  const style = css`
    & {
      border: none;
      width: 100%;
    }

    & td {
      padding: 0.6rem 0;
    }

    & td,
    & th {
      border: none;
      padding-left: 0 !important;
    }

    & thead,
    & tr,
    & th {
      background-color: transparent !important;
      color: ${COLORS.dark4};
      font-size: ${fontSizes.small}rem;
    }
  `

  return <table className="pure-table" css={style} {...props} />
}

export default Table
